import { useState, useEffect, useCallback } from 'react';
import { Activity, DollarSign, AlertCircle, AlertOctagon } from 'lucide-react';
import { api } from '@/lib/api';
import type { AdminTab } from './shared/constants';

interface HealthData {
  activeJobs: number;
  todaySpend: number;
  unresolvedErrors: number;
  criticalErrors: number;
}

interface AdminHealthBarProps {
  onNavigate: (tab: AdminTab) => void;
  activeTab: AdminTab;
}

export function AdminHealthBar({ onNavigate, activeTab }: AdminHealthBarProps) {
  const [data, setData] = useState<HealthData | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [errorSummary, activeGens, usageSummary] = await Promise.all([
        api.getAdminErrorSummary(24),
        api.getActiveGenerations(),
        api.getApiUsageSummary({ start_date: today, end_date: today }),
      ]);
      setData({
        activeJobs: activeGens.count,
        todaySpend: usageSummary.summary.total_cost_usd,
        unresolvedErrors: errorSummary.unresolved,
        criticalErrors: errorSummary.recent_critical,
      });
    } catch (err) {
      console.error('Failed to fetch health data:', err);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const indicators = [
    {
      label: 'Active Jobs',
      value: data?.activeJobs ?? '—',
      icon: Activity,
      tab: 'operations' as AdminTab,
      color: data && data.activeJobs > 0 ? 'text-primary' : 'text-text-muted',
      bgColor: data && data.activeJobs > 0 ? 'bg-primary/15' : 'bg-surface-light',
    },
    {
      label: "Today's Spend",
      value: data ? `$${data.todaySpend.toFixed(2)}` : '—',
      icon: DollarSign,
      tab: 'cost-api' as AdminTab,
      color: 'text-success',
      bgColor: 'bg-success/15',
    },
    {
      label: 'Unresolved',
      value: data?.unresolvedErrors ?? '—',
      icon: AlertCircle,
      tab: 'errors' as AdminTab,
      color: data && data.unresolvedErrors > 0 ? 'text-error' : 'text-success',
      bgColor: data && data.unresolvedErrors > 0 ? 'bg-error/15' : 'bg-success/15',
    },
    {
      label: 'Critical',
      value: data?.criticalErrors ?? '—',
      icon: AlertOctagon,
      tab: 'errors' as AdminTab,
      color: data && data.criticalErrors > 0 ? 'text-error' : 'text-text-muted',
      bgColor: data && data.criticalErrors > 0 ? 'bg-error/20' : 'bg-surface-light',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {indicators.map((ind) => (
        <button
          key={ind.label}
          onClick={() => onNavigate(ind.tab)}
          className={`flex items-center gap-3 rounded-xl border p-3 transition-all hover:scale-[1.02] ${
            activeTab === ind.tab
              ? 'border-primary/40 bg-primary/5'
              : 'border-border bg-surface hover:border-border-hover'
          }`}
        >
          <div className={`w-9 h-9 rounded-lg ${ind.bgColor} flex items-center justify-center shrink-0`}>
            <ind.icon className={`w-4 h-4 ${ind.color}`} />
          </div>
          <div className="text-left min-w-0">
            <p className="text-lg font-bold leading-tight">{ind.value}</p>
            <p className="text-xs text-text-muted truncate">{ind.label}</p>
          </div>
        </button>
      ))}
    </div>
  );
}
